import dayjs from 'dayjs'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column, ColumnSortParams } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Tooltip } from 'primereact/tooltip'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JournalRythmCt } from '../../../model/enums/enums'
import { learningJournal } from '../../../model/learnJournal.model'
import { Apprentice, Person } from '../../../model/person.model'
import { useAppSelector } from '../../../redux/hooks'
import { AuthRoutes } from '../../../router/routes'
import { findApprenticeById, findPersonById } from '../../../util/arraySearch'
import { AvatarCell } from '../../util/data-table/cell-body/FullNameApprenticeBody'

export const JournalDataTable = (props: {
  data: learningJournal[]
  currentApprentice: Apprentice | undefined
  currentEmployee: Person | undefined
  concluded: boolean
}): JSX.Element => {
  const apprentices = useAppSelector((state) => state.apprentices)
  const employees = useAppSelector((state) => state.employees)
  const navigate = useNavigate()

  const [currentDate] = useState(dayjs())

  const routeToJournal = (body: learningJournal) => {
    navigate(`${AuthRoutes.journals}/${body.learning_journal_id}`)
  }
  const apprenticeAvatar = (journalBody: learningJournal) => {
    const app = findApprenticeById(journalBody.id_apprentice, apprentices)
    return AvatarCell(app)
  }

  const employeeAvatar = (journalBody: learningJournal) => {
    const app = findPersonById(journalBody.id_responsible, employees)
    return AvatarCell(app)
  }

  const apprenticeSortFunction = (sp: ColumnSortParams) => {
    const sortableJournals: learningJournal[] = sp.data
    return sortableJournals.sort((a: any, b: any) => {
      const apprentice_a = apprentices.find(
        (app) => app.apprentice?.apprentice_id === a.id_apprentice
      )?.full_name
      const apprentice_b = apprentices.find(
        (app) => app.apprentice?.apprentice_id === b.id_apprentice
      )?.full_name
      if (!apprentice_a || !apprentice_b) return 0

      if (sp.order === 1) return apprentice_a > apprentice_b ? -1 : 1
      else return apprentice_a < apprentice_b ? -1 : 1
    })
  }

  const employeeSortFunction = (sp: ColumnSortParams) => {
    const sortableJournals: learningJournal[] = sp.data
    return sortableJournals.sort((a: any, b: any) => {
      const apprentice_a = employees.find(
        (e) => e.person_id === a.id_responsible
      )?.full_name
      const apprentice_b = employees.find(
        (e) => e.person_id === b.id_responsible
      )?.full_name
      if (!apprentice_a || !apprentice_b) return 0

      if (sp.order === 1) return apprentice_a > apprentice_b ? -1 : 1
      else return apprentice_a < apprentice_b ? -1 : 1
    })
  }

  const getHandInTypeOfApprentice = (apprenticeId: number | undefined) => {
    if (!apprenticeId) {
      return JournalRythmCt.Weekly
    }
    const app = findApprenticeById(apprenticeId, apprentices)
    return app?.apprentice?.journal_rythm || JournalRythmCt.Weekly
  }

  const isWeeklyOnTime = (handInRhytm: number, cw: number): boolean => {
    return handInRhytm === JournalRythmCt.Weekly && cw === currentDate.week()
  }
  const isMonthlyOnTIme = (handInRhytm: number, mth: number): boolean => {
    return handInRhytm === JournalRythmCt.Monthly && mth === currentDate.month()
  }

  const dateWithCW = (body: learningJournal) => {
    const date = dayjs(body.entry_date)
    const text = date.format(t<string>('locales.dateFormat'))
    const cw = date.week()
    const mth = date.month()
    const handInRhytm = getHandInTypeOfApprentice(body?.id_apprentice)

    // finished or in future
    if (body.completed || cw > currentDate.week())
      return (
        <>
          <span className={'ml-1 mr-2 fa-solid fa-circle icon-green'} />
          {text} ({t<string>('core.calendary_week.short')} {cw})
        </>
      )

    // still time to finish it on time
    if (isWeeklyOnTime(handInRhytm, cw) || isMonthlyOnTIme(handInRhytm, mth)) {
      return (
        <div className={'tooltip-current-week'}>
          <span className={'ml-1 mr-2 fa-solid fa-star icon-orange'} />
          <span>
            <>
              {text} ({t<string>('core.calendary_week.short')} {cw}
              {handInRhytm === JournalRythmCt.Monthly && (
                <>, {date.format('MMM')}</>
              )}
              )
            </>
          </span>
        </div>
      )
    }
    // the journal is too late it either needs action by the apprentice or by the responsible
    else {
      return (
        <div className={'tooltip-week-in-past'}>
          <span className={'ml-1 mr-2 fa-solid fa-clock icon-red'} />
          <span>
            {text} ({t<string>('core.calendary_week.short')} {cw})
          </span>
        </div>
      )
    }
  }

  const getIcon = (body: learningJournal) => {
    return body.is_locked ? (
      <Button
        className={'p-button-text icon-orange'}
        tooltipOptions={{
          style: { minWidth: '100px' },
          showDelay: 500,
          position: 'left',
        }}
        onClick={() => routeToJournal(body)}
        icon={'fa-solid fa-eye'}
        tooltip={t<string>('journal.symbols.review')}
      />
    ) : (
      <Button
        className={'p-button-text icon-blue'}
        tooltipOptions={{
          style: { minWidth: '100px' },
          showDelay: 500,
          position: 'left',
        }}
        onClick={() => routeToJournal(body)}
        icon={'fa-solid fa-pen'}
        tooltip={t<string>('journal.symbols.edit')}
      />
    )
  }

  const checkIcon = (body: learningJournal) => {
    return (
      <Button
        className={'p-button-text icon-green'}
        onClick={() => routeToJournal(body)}
        tooltip={t<string>('journal.symbols.completed')}
        tooltipOptions={{
          style: { minWidth: '100px' },
          showDelay: 500,
          position: 'left',
        }}
        icon={'fa-solid fa-check-circle'}
      />
    )
  }

  return (
    <>
      <Tooltip
        content={t<string>('core.calendary_week.current_week')}
        target={'.tooltip-current-week'}
        position={'left'}
        showDelay={500}
      />
      <Tooltip
        content={t<string>('core.calendary_week.week_in_past')}
        target={'.tooltip-week-in-past'}
        position={'left'}
        showDelay={500}
      />

      <DataTable
        showGridlines
        stripedRows
        className={'p-datatable-sm ml-0 pl-0 autoscroll'}
        value={props.data}
        rowHover
        scrollable
        scrollHeight="600px"
        sortField={'entry_date'}
        sortOrder={1}
        sortMode="single"
        removableSort
        emptyMessage={t<string>('core.empty_message')}
      >
        <Column
          header={t<string>('core.apprentices')}
          field={'id_apprentice'}
          filterField={'id_apprentice'}
          body={(val: learningJournal) => apprenticeAvatar(val)}
          sortable
          sortFunction={(e: ColumnSortParams) => apprenticeSortFunction(e)}
        />
        <Column
          header={t<string>('journal.entry_date')}
          field={'entry_date'}
          filterField={'entry_date'}
          dataType={'date'}
          sortable
          body={dateWithCW}
        />
        <Column
          header={t<string>('core.responsible')}
          field={'id_responsible'}
          body={(val: learningJournal) => employeeAvatar(val)}
          sortable
          sortFunction={(e: ColumnSortParams) => employeeSortFunction(e)}
        />
        <Column
          header={t<string>('core.status')}
          field={'is_locked'}
          sortable
          body={(e) => (!props.concluded ? getIcon(e) : checkIcon(e))}
        />
      </DataTable>
    </>
  )
}